/** Power-ups (add-ons) picked up in the run: timers, charges and their gameplay effects. Pure. */

export type PowerupKind = 'feather' | 'shield' | 'magnet' | 'boots';
export const POWERUP_KINDS: readonly PowerupKind[] = ['feather', 'shield', 'magnet', 'boots'];

export const POWERUPS = {
  /** One extra mid-air jump per airtime while active. */
  feather: { durationMs: 20000, airJumps: 1, label: 'double jump' },
  /** Absorbs one hazard hit. */
  shield: { durationMs: 30000, label: 'shield' },
  /** Pulls bones within radius toward Georgi. */
  magnet: { durationMs: 15000, radiusPx: 240, pullPxPerS: 720, label: 'magnet' },
  /** Faster walk and run. */
  boots: { durationMs: 12000, speedScale: 1.3, label: 'speed boots' },
} as const;

export interface PowerupState {
  /** ms left per kind (0 = inactive). */
  left: Record<PowerupKind, number>;
  /** Air jumps spent since the last landing. */
  airJumpsUsed: number;
}

export function createPowerups(): PowerupState {
  return { left: { feather: 0, shield: 0, magnet: 0, boots: 0 }, airJumpsUsed: 0 };
}

export function activate(s: PowerupState, kind: PowerupKind): PowerupState {
  return { ...s, left: { ...s.left, [kind]: POWERUPS[kind].durationMs } };
}

export function tickPowerups(s: PowerupState, dtMs: number): PowerupState {
  const left = { ...s.left };
  for (const kind of POWERUP_KINDS) left[kind] = Math.max(0, left[kind] - dtMs);
  return { ...s, left };
}

export function isActive(s: PowerupState, kind: PowerupKind): boolean {
  return s.left[kind] > 0;
}

/** Extra mid-air jumps available right now. */
export function airJumpsAvailable(s: PowerupState): number {
  if (!isActive(s, 'feather')) return 0;
  return Math.max(0, POWERUPS.feather.airJumps - s.airJumpsUsed);
}

export function useAirJump(s: PowerupState): PowerupState {
  return { ...s, airJumpsUsed: s.airJumpsUsed + 1 };
}

export function landed(s: PowerupState): PowerupState {
  return s.airJumpsUsed === 0 ? s : { ...s, airJumpsUsed: 0 };
}

/** A hazard hit: the shield takes it (and ends) when active. */
export function absorbHit(s: PowerupState): { state: PowerupState; absorbed: boolean } {
  if (!isActive(s, 'shield')) return { state: s, absorbed: false };
  return { state: { ...s, left: { ...s.left, shield: 0 } }, absorbed: true };
}

export function speedScale(s: PowerupState): number {
  return isActive(s, 'boots') ? POWERUPS.boots.speedScale : 1;
}

export function magnetRadiusPx(s: PowerupState): number {
  return isActive(s, 'magnet') ? POWERUPS.magnet.radiusPx : 0;
}

/** Active kinds with seconds left, for the HUD. */
export function activeList(s: PowerupState): { kind: PowerupKind; secondsLeft: number }[] {
  return POWERUP_KINDS.filter((k) => s.left[k] > 0).map((kind) => ({
    kind,
    secondsLeft: Math.ceil(s.left[kind] / 1000),
  }));
}
